import { useCallback, useEffect, useState } from "react";
import {
  ExportableVisibility,
  getPlayEvidenceExport,
  getPlaySignals,
  PlayEvidenceExportResponse,
  PlaySignals,
  PlaySignalsRange,
} from "../../api/playAdminApi";
import { ApiError } from "../../services/restApiService";

export type PlayPageState =
  | "loading"
  | "ready"
  | "dormant" // 404 play_not_enabled — the environment kill-switch (prod posture)
  | "denied" // entitlement 403 — org package excludes PLAY_SANDBOX
  | "error";

const classify = (err: unknown): PlayPageState => {
  if (err instanceof ApiError) {
    if (err.status === 404 && err.errorCode === "play_not_enabled") {
      return "dormant";
    }
    if (err.status === 403) return "denied";
  }
  return "error";
};

export const usePlaySignalsHook = () => {
  const [range, setRange] = useState<PlaySignalsRange>("30d");
  const [signals, setSignals] = useState<PlaySignals | null>(null);
  const [pageState, setPageState] = useState<PlayPageState>("loading");

  const [exportVisibility, setExportVisibility] = useState<
    ExportableVisibility | ""
  >("");
  const [exportOffset, setExportOffset] = useState(0);
  const [exportData, setExportData] =
    useState<PlayEvidenceExportResponse | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const loadSignals = useCallback(async (selectedRange: PlaySignalsRange) => {
    setPageState("loading");
    try {
      const data = await getPlaySignals(selectedRange);
      setSignals(data);
      setPageState("ready");
    } catch (err) {
      setSignals(null);
      setPageState(classify(err));
    }
  }, []);

  const loadExport = useCallback(
    async (visibility: ExportableVisibility | "", offset: number) => {
      setExportLoading(true);
      try {
        const data = await getPlayEvidenceExport({
          ...(visibility ? { visibility } : {}),
          limit: 50,
          offset,
        });
        setExportData(data);
      } catch {
        // The page-level state (dormant/denied) is driven by the signals
        // call; export errors keep the previous table rather than blanking.
      } finally {
        setExportLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSignals(range);
  }, [range, loadSignals]);

  useEffect(() => {
    if (pageState === "ready") loadExport(exportVisibility, exportOffset);
  }, [pageState, exportVisibility, exportOffset, loadExport]);

  const changeRange = (next: PlaySignalsRange) => setRange(next);
  const changeExportVisibility = (next: ExportableVisibility | "") => {
    setExportVisibility(next);
    setExportOffset(0);
  };

  return {
    range,
    changeRange,
    signals,
    pageState,
    retry: () => loadSignals(range),
    exportVisibility,
    changeExportVisibility,
    exportOffset,
    setExportOffset,
    exportData,
    exportLoading,
  };
};
